import React, {useCallback, useEffect, useState} from "react";
import {
  Typography,
  Box,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import "./stationDetails.scss";
import { useHistory, useLocation } from "react-router";
import { FireDangerTab } from "./FireDangerTab";
import { CatalogTab } from "./CatalogTab";
import { StationMetaDataTab } from "./StationMetaDataTab";
import { ChartsTab } from "./ChartsTab";
import { ROUTE_PATHS } from "src/routes";
import MapIcon from '@mui/icons-material/Map';
import { useStoreContext } from "src/store/context";
import { useAddAlert, useAllStations, useChartTableSelect, useElementRadioSelect, useElementSelection, 
  useMapDataType, } from "src/store/actions";
import { Station } from "src/types";
import localforage from "localforage";
import { getStationMeta, getStations } from "src/apis/getWeatherCalls";
import { nfdrRadioButtons, weatherRadioButtons } from "src/constants";
import { buildPath } from "src/store/common";
import { WxTab } from "./WxTab";
import { getAccessTokenCookie } from "src/shared/Login/AuthProvider";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box >{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function StationDetails({tab}) {
  const history = useHistory();
  const location = useLocation();
  const [state, dispatch] = useStoreContext();
  const allStoredStations = useAllStations(dispatch);
	const addAlert = useAddAlert(dispatch);

  const setMapData = useMapDataType(dispatch);
  const selectChartTableType = useChartTableSelect(dispatch);
  const setRadio = useElementRadioSelect(dispatch);
  const setElement = useElementSelection(dispatch);

  const [tabValue, setTabValue] = useState(tab);
  const [station, setStation] = useState<any>({station_metadata_visible: 'hmm'});
  const [routes, setRoutes ] = useState<string[]>([]);
  const [searchedStation, setSearchedStation] = useState<any>({search_name: ''});
  const [viewMode, setViewMode]  = useState("hourly");
  const cookieToken = getAccessTokenCookie(`accessToken`);
  const [stationFailCount, setStationFailCount ] = useState<number>(0);

  const [stationListFailCount, setStationListFailCount ] = useState<number>(0);


  const stationMeta = useCallback((id: any, counter:number) => {
    (async () => {
      try {
        const varRequest = {
          stationId: id.toString(),
        };
        const response: any = await getStationMeta(varRequest, addAlert, state?.user);
        if (response) {
          setStation(response);
          setStationFailCount(0);
        }
        else{
          setStation(null);
        }
      }
      catch (error) {
        console.log('error', error)
        setStation(null)
        setStationFailCount(counter+1)
      }
    })();
  }, [addAlert, state?.user, setStationFailCount]);


  useEffect(()=>{
    
    if(!cookieToken && !station?.station_metadata_visible){
      history.push(`${ROUTE_PATHS.table}`)
      
    }
    
  },[cookieToken, station, history])

  useEffect(()=>{
    if(location?.state){
      const thisStation:any = location.state;
      if(thisStation.from){
        setSearchedStation(thisStation)
      }
      setStation(thisStation);
      setRoutes([`${ROUTE_PATHS.table}/fireDanger/${thisStation.station_id}`, 
      `${ROUTE_PATHS.table}/weather/${thisStation.station_id}`, 
      `${ROUTE_PATHS.table}/stationMetaData/${thisStation.station_id}`,
      `${ROUTE_PATHS.table}/catalog/${thisStation.station_id}`,
      `${ROUTE_PATHS.table}/charts/${thisStation.station_id}`
      ])
    }
    else{
      const stringArray = decodeURIComponent(location.pathname).split("/");
      const stationId = stringArray[3];
      stationMeta(stationId, stationFailCount);
      setRoutes([`${ROUTE_PATHS.table}/fireDanger/${stationId}`,
      `${ROUTE_PATHS.table}/weather/${stationId}`, 
      `${ROUTE_PATHS.table}/stationMetaData/${stationId}`,
      `${ROUTE_PATHS.table}/catalog/${stationId}`,
      `${ROUTE_PATHS.table}/charts/${stationId}`
      ])
    }
  },[location?.pathname, location?.state, stationMeta, stationFailCount]);
  
  useEffect(()=>{
    if(stationFailCount>0 && stationFailCount<4){
      if(location?.state){
        const thisStation:any = location.state;
        if(thisStation.from){
          setSearchedStation(thisStation)
        }
        setStation(thisStation);
        setRoutes([`${ROUTE_PATHS.table}/fireDanger/${thisStation.station_id}`, 
        `${ROUTE_PATHS.table}/weather/${thisStation.station_id}`, 
        `${ROUTE_PATHS.table}/stationMetaData/${thisStation.station_id}`,
        `${ROUTE_PATHS.table}/catalog/${thisStation.station_id}`,
        `${ROUTE_PATHS.table}/charts/${thisStation.station_id}`
        ])
      }
      else{
        const stringArray = decodeURIComponent(location.pathname).split("/");
        const stationId = stringArray[3];
        stationMeta(stationId, stationFailCount);
        setRoutes([`${ROUTE_PATHS.table}/fireDanger/${stationId}`,
        `${ROUTE_PATHS.table}/weather/${stationId}`, 
        `${ROUTE_PATHS.table}/stationMetaData/${stationId}`,
        `${ROUTE_PATHS.table}/catalog/${stationId}`,
        `${ROUTE_PATHS.table}/charts/${stationId}`
        ])
      }
    }
  },[stationFailCount, stationMeta, location?.pathname, location?.state, setStationFailCount]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    history.push({
      pathname: `${routes[newValue]}`,
      state: {...station}
    });
  };

  const stationCallback = useCallback((counter:number) => {
    (async () => {
      try {
        const varRequest = {
          returnAll: true,
        };

        const response: any = await getStations(varRequest, addAlert, state.user);
        if (response?.stations) {
          const stations = response.stations.sort(function (a: any, b: any) {
            return a.search_name.localeCompare(b.search_name);
          })
          allStoredStations(stations)
          setStationListFailCount(0);
        }
        else{
          setStationListFailCount(counter+1);
        }
      }
      catch (error) {
        setStationListFailCount(counter+1)
        console.log('error', error)
      }
    })();
  }, [addAlert, allStoredStations, state?.user]);

  useEffect(() => {
    if (state.all_stations?.length < 1) {
      localforage.getItem('allStations').then(async function (stations) {
        let allStations = stations as Station[];
        if (allStations) {
          allStoredStations(allStations);        
        }
        else {
          stationCallback(stationListFailCount);
        }
      })
    }
  }, [state.all_stations?.length,stationListFailCount, allStoredStations, stationCallback]);

  useEffect(()=>{
    if(stationListFailCount>0 && stationListFailCount){
      stationCallback(stationListFailCount);
    }
  },[stationListFailCount, stationCallback]);

  const clickViewAllStations = ()=>{
    history.push({
      pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.stations}`,
      state: {...station, page: 0, perPage: 10}
    })

  };
  
/**
 * For stations without historic data we want to reset the tab the user is on only if they were on the charts tab previously
 */
  const onStationSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
    if(incomingVal && state.all_stations?.length>1){
      const findStation = state.all_stations.find((station)=>station.search_name === incomingVal?.search_name);
      const stringArray = decodeURIComponent(location?.pathname).split("/");
      if(((findStation?.period_record_start && findStation?.period_record_stop && findStation?.has_historic_data))){
        history.push({ pathname: `${ROUTE_PATHS.table}/${stringArray[2]}/${findStation?.station_id}`, state: {...findStation, from: 'search'} });
      } else {
          if(tabValue===4){//season trend charts tab
            setTabValue(0);
            history.push({pathname: `${ROUTE_PATHS.table}/fireDanger/${findStation?.station_id}`, state: {...findStation, from: 'search'}});

          }
          else{
            history.push({ pathname: `${ROUTE_PATHS.table}/${stringArray[2]}/${findStation?.station_id}`, state: {...findStation, from: 'search'} });
          }
      }
    }
  }

  const viewOnMap = ()=>{
    if(tabValue===0){
      if(viewMode==='hourly'){
        setMapData('FireDanger');
        setRadio(nfdrRadioButtons[0]);
        setElement(nfdrRadioButtons[0].elementSelections[6]);
        selectChartTableType(nfdrRadioButtons[0].elementSelections[6].defaultChart)
        const paramsForBuildPath = {
            dataType: 'FireDanger',
            radio: nfdrRadioButtons[0].type,
            element: nfdrRadioButtons[0].elementSelections[6].value,
            zoom: state.currentZoom,
            basemap: state.selectedBaseMap.id,
            minElevation: state.elevationFilterValues[0],
            maxElevation: state.elevationFilterValues[1],
            windOrMethod: state.nfdrFuelModel,
            windOrSub: false
        }
        buildPath(paramsForBuildPath, history, station.station_id);

      }
      else{
        setMapData('FireDanger');
        setRadio(nfdrRadioButtons[1]);
        setElement(nfdrRadioButtons[1].elementSelections[10]);
        selectChartTableType(nfdrRadioButtons[1].elementSelections[10].defaultChart)
        const paramsForBuildPath = {
            dataType: 'FireDanger',
            radio: nfdrRadioButtons[1].type,
            element: nfdrRadioButtons[1].elementSelections[10].value,
            zoom: state.currentZoom,
            basemap: state.selectedBaseMap.id,
            minElevation: state.elevationFilterValues[0],
            maxElevation: state.elevationFilterValues[1],
            windOrMethod: state.nfdrFuelModel,
            windOrSub: false
        }
        buildPath(paramsForBuildPath, history, station.station_id);

      }
    }
    else if(tabValue===1){
      if(viewMode==='hourly'){

        setMapData('Weather');
        setRadio(weatherRadioButtons[0]);
        setElement(weatherRadioButtons[0].elementSelections[0]);
        selectChartTableType(weatherRadioButtons[0].elementSelections[0].defaultChart)
        const paramsForBuildPath = {
            dataType: 'Weather',
            radio: weatherRadioButtons[0].type,
            element: weatherRadioButtons[0].elementSelections[0].value,
            zoom: state.currentZoom,
            basemap: state.selectedBaseMap.id,
            minElevation: state.elevationFilterValues[0],
            maxElevation: state.elevationFilterValues[1],
            windOrMethod: false,
            windOrSub: false
        }
        buildPath(paramsForBuildPath, history, station.station_id);
      }
      else{
        setMapData('Weather');
        setRadio(weatherRadioButtons[1]);
        setElement(weatherRadioButtons[1].elementSelections[0]);
        selectChartTableType(weatherRadioButtons[1].elementSelections[0].defaultChart)
        const paramsForBuildPath = {
            dataType: 'Weather',
            radio: weatherRadioButtons[1].type,
            element: weatherRadioButtons[1].elementSelections[0].value,
            zoom: state.currentZoom,
            basemap: state.selectedBaseMap.id,
            minElevation: state.elevationFilterValues[0],
            maxElevation: state.elevationFilterValues[1],
            windOrMethod: false,
            windOrSub: false
        }
        buildPath(paramsForBuildPath, history, station.station_id);
      }
    }
    else{
        setMapData('FireDanger');
        setRadio(nfdrRadioButtons[1]);
        setElement(nfdrRadioButtons[1].elementSelections[10]);
        selectChartTableType(nfdrRadioButtons[1].elementSelections[10].defaultChart)
        const paramsForBuildPath = {
            dataType: 'FireDanger',
            radio: nfdrRadioButtons[1].type,
            element: nfdrRadioButtons[1].elementSelections[10].value,
            zoom: state.currentZoom,
            basemap: state.selectedBaseMap.id,
            minElevation: state.elevationFilterValues[0],
            maxElevation: state.elevationFilterValues[1],
            windOrMethod: state.nfdrFuelModel,
            windOrSub: false
        }
        buildPath(paramsForBuildPath, history, station.station_id);
      }
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 2,
        }}
      >
        {/* Left Section */}
        <Box>
          <Typography variant="h5">
            <span style={{marginRight: '1rem', textTransform:'capitalize'}}>{station?.station_name}</span> 
            <Button variant="text" style={{padding:0, textDecorationLine: 'underline', fontSize: '1rem',textTransform: 'capitalize'}} onClick={viewOnMap}>
              View On Map<MapIcon style={{fontSize: 'medium'}}/>
            </Button>
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 0.5 }}>
            <strong>Network:</strong> {station?.network_name} |{" "}
            <strong>State:</strong> {station?.state} | {" "}
            <strong>Agency Owner:</strong> {station?.agency}
          </Typography>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: "flex", flexDirection: "column", marginRight: '1rem',alignItems: "flex-end", width: '25vw' }}>
          <div className="searchStationsDownloadPage">
            {/* Search Icons */}
            <i className="fas fa-search"/>
            <Autocomplete
              id="station"
              clearOnEscape
              freeSolo
              noOptionsText={
                <div className='no-results'>
                    <div>No results match your search criteria.</div>
                    <div><b>Here are a few tips:</b></div>
                    <div>• Remember to check your spelling<br />
                        • Ensure you are searching either a Station Name or Station ID number</div>
                </div>
              }
              options={state.all_stations? state.all_stations:[]}
              isOptionEqualToValue={(option,value)=>option?.search_name===value?.search_name}
              getOptionLabel={(option)=>`${option.search_name}`}
              filterOptions={(options, {inputValue})=>{
                  const search = inputValue.toLowerCase();
                  if(search.length<=2){
                      return options.filter((option)=>
                          option.state?.toLowerCase().includes(search) ||
                          option.station_id.toString().includes(search)

                      )
                  }
                  return options.filter((option)=>
                      option.station_name?.toLowerCase().includes(search) ||
                      option.network_name?.toLowerCase().includes(search) ||
                      option.state?.toLowerCase().includes(search) ||
                      option.station_id.toString().includes(search)

                  )
              }}             
              aria-label="stations"
              value={searchedStation}
              onChange={(event, newValue) => onStationSearch(event, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={'Search All Stations...'}
                  placeholder="Search Station..."
                />
              )}
            />
          </div>
          <Box mt={1}>
            <Button variant='text' onClick={clickViewAllStations} className="font-sans-sm text-bold" style={{ color: "#1675e0", textTransform:'capitalize' }}>
              View All Stations
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: "100%", marginTop: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        {/* Tabs Section */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="custom tabs example"
            className="custom-tabs"
          >
            <Tab label="Fire Danger" {...a11yProps(0)} />
            <Tab label="Weather" {...a11yProps(1)} />
            <Tab label="Station Metadata" {...a11yProps(2)} />
            <Tab label="Catalog" {...a11yProps(3)} />
            {
              (!station?.period_record_start || !station?.period_record_stop || !station?.has_historic_data) 
                && <Tab label="Seasonal Trend Charts" {...a11yProps(3)} disabled/>
            }
            {
              station?.period_record_start && station?.period_record_stop && station?.has_historic_data
              && <Tab label="Seasonal Trend Charts" {...a11yProps(3)} />
            }
          </Tabs>
        </Box>
        {/* Tab Panels */}
        <CustomTabPanel value={tabValue} index={0}>
          <FireDangerTab station={station?.station_metadata_visible!=='hmm' && station} viewMode={viewMode}  setViewMode={setViewMode}/>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <WxTab station={station?.station_metadata_visible!=='hmm' && station} viewMode={viewMode}  setViewMode={setViewMode}/>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <div style={{backgroundColor: '#dddddd', height: '60vh'}}>
            <StationMetaDataTab station={station?.station_metadata_visible!=='hmm' && station} />
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={3}>
          <div style={{backgroundColor: '#eeeeee', height: '100%'}}>
            <CatalogTab station={station?.station_metadata_visible!=='hmm' && station} />
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={4}>
          <ChartsTab station={station?.station_metadata_visible!=='hmm' && station} />
        </CustomTabPanel>
      </Box>

    </Box>
  );
}

export default StationDetails;